import React, { useState, useEffect } from 'react';
import { databases, ensureAuth, ID } from '../services/appwriteClient';
import { Query } from 'appwrite';
import { Student } from '../types';
import { Download, Edit2 } from 'lucide-react';
import { updateAttendance, getAttendanceRecord } from '../services/attendanceService';
import { getStudents } from '../services/studentService';

// Types
interface AttendanceRecord {
  id: string;
  student_id: string;
  Status: 'present' | 'absent' | 'late';
  date: string;
  Created_at: string;
  Updated_at: string;
  remarks?: string;
}

interface LevelSummary {
  level: string;
  totalStudents: number;
  presentCount: number;
  absentCount: number;
  students: Array<{
    id: string;
    name: string;
    status: 'present' | 'absent';
    registerNumber?: string;
  }>;
}

interface StudentHistory {
  date: string;
  status: 'present' | 'absent';
}

// Add new interface for export data
interface MonthlyAttendanceData {
  studentId: string;
  studentName: string;
  level: string;
  registerNumber?: string;
  attendance: {
    date: string;
    status: 'present' | 'absent';
  }[];
}

interface StudentSummary {
  present: number;
  late: number;
  absent: number;
  total: number;
}

// Constants
const DATABASE_ID = '682acd26001f69683cd8';
const STUDENTS_COLLECTION_ID = '682acd33000e34dd93c0';
const ATTENDANCE_COLLECTION_ID = '683078a400301e04d0e8';

interface StudentWithAttendance extends Student {
  attendance: {
    id: string;
    Status: string;
    date: string;
    Created_at: string;
    Updated_at: string;
    remarks: string;
  }[];
}

// Subcomponent: LevelSummarySection
const LevelSummarySection: React.FC<{
  summary: LevelSummary;
  onStudentClick: (studentId: string) => void;
}> = ({ summary, onStudentClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white rounded-lg shadow-md mb-4">
      <div 
        className="p-4 cursor-pointer flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <h3 className="text-lg font-semibold">{summary.level}</h3>
          <div className="flex gap-4 mt-1">
            <span className="text-sm text-gray-600">Total: {summary.totalStudents}</span>
            <span className="text-sm text-green-600">Present: {summary.presentCount}</span>
            <span className="text-sm text-red-600">Absent: {summary.absentCount}</span>
          </div>
        </div>
        <button className="text-gray-500">
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t">
          {summary.students.map((student) => (
            <div
              key={student.id}
              className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
              onClick={() => onStudentClick(student.id)}
            >
              <div>
                <span className="font-medium">{student.name}</span>
                {student.registerNumber && (
                  <span className="text-sm text-gray-500 ml-2">({student.registerNumber})</span>
                )}
              </div>
              <span className={`px-2 py-1 rounded text-sm ${
                student.status === 'present' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {student.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Subcomponent: StudentHistoryModal
const StudentHistoryModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
}> = ({ isOpen, onClose, studentId, studentName }) => {
  const [history, setHistory] = useState<StudentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchStudentHistory();
    }
  }, [isOpen, studentId]);

  const fetchStudentHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const response = await databases.listDocuments(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        [
          Query.equal('student_id', studentId),
          Query.greaterThanEqual('date', thirtyDaysAgo.toISOString().split('T')[0]),
          Query.orderDesc('date')
        ]
      );

      setHistory(response.documents.map((doc: any) => ({
        date: doc.date,
        status: doc.Status // Use capital 'Status' as per Appwrite schema
      })));
    } catch (err) {
      console.error('Error fetching student history:', err);
      setError('Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{studentName}'s Attendance History</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center">{error}</div>
          ) : (
            <div className="space-y-2">
              {history.map((record) => (
                <div
                  key={record.date}
                  className="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                >
                  <span className="text-gray-600">{new Date(record.date).toLocaleDateString()}</span>
                  <span className={`px-2 py-1 rounded text-sm ${
                    record.status === 'present' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {record.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add export functions
const generateCSV = (data: MonthlyAttendanceData[]) => {
  // Get all unique dates from the data
  const allDates = Array.from(
    new Set(
      data.flatMap(student => 
        student.attendance.map(record => record.date)
      )
    )
  ).sort();

  // Create CSV header
  const header = ['Student ID', 'Name', 'Level', 'Register Number', ...allDates].join(',');
  
  // Create CSV rows
  const rows = data.map(student => {
    const attendanceMap = new Map(
      student.attendance.map(record => [record.date, record.status])
    );
    
    const attendanceStatus = allDates.map(date => 
      attendanceMap.get(date) || 'N/A'
    );
    
    return [
      student.studentId,
      `"${student.studentName}"`,
      student.level,
      student.registerNumber || '',
      ...attendanceStatus
    ].join(',');
  });

  return [header, ...rows].join('\n');
};

const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Main Component
const AttendanceSummaryPage: React.FC = () => {
  const [students, setStudents] = useState<StudentWithAttendance[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<{
    studentId: string;
    status: 'present' | 'absent' | 'late';
    remarks?: string;
  } | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Get available groups based on selected level
  const getAvailableGroups = () => {
    return [
      { value: 'larva1', label: 'Larva 1' },
      { value: 'larva2', label: 'Larva 2' },
      { value: 'larva3', label: 'Larva 3' },
      { value: 'pupa1', label: 'Pupa 1' },
      { value: 'pupa2', label: 'Pupa 2' },
      { value: 'pupa3', label: 'Pupa 3' },
      { value: 'caterpillar1', label: 'Caterpillar 1' },
      { value: 'caterpillar2', label: 'Caterpillar 2' },
      { value: 'caterpillar3', label: 'Caterpillar 3' },
      { value: 'butterfly', label: 'Butterfly' }
    ];
  };

  // Reset group selection when level changes
  useEffect(() => {
    setSelectedGroup('all');
  }, [selectedLevel]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      await ensureAuth();

      // Fetch all students using the shared function
      const allStudents = await getStudents();
      console.log('Attendance Summary - Total students fetched:', allStudents.length);

      // Get attendance records for the selected date with increased limit
      const attendanceResponse = await databases.listDocuments(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        [
          Query.equal('date', selectedDate),
          Query.limit(500) // Increased limit to handle large number of students
        ]
      );

      console.log('Attendance Summary - Attendance records fetched:', attendanceResponse.documents.length);
      console.log('Attendance Summary - Date filter:', selectedDate);

      // Create a map for faster lookup
      const attendanceMap = new Map();
      attendanceResponse.documents.forEach((doc: any) => {
        attendanceMap.set(doc.student_id, {
          id: doc.$id,
          Status: doc.Status,
          date: doc.date,
          remarks: doc.remarks || '',
          Created_at: doc.Created_at,
          Updated_at: doc.Updated_at
        });
      });

      console.log('Attendance Summary - Attendance map size:', attendanceMap.size);

      // Map students with their attendance
      const studentsWithAttendance = allStudents.map(student => {
        const studentAttendance = attendanceMap.get(student.id);

        if (studentAttendance) {
          console.log(`Student ${student.name} (${student.id}): ${studentAttendance.Status}`);
        } else {
          console.log(`Student ${student.name} (${student.id}): Not Marked`);
        }

        return {
          ...student,
          attendance: studentAttendance ? [studentAttendance] : []
        };
      });

      // Count marked vs not marked
      const markedCount = studentsWithAttendance.filter(s => s.attendance.length > 0).length;
      const notMarkedCount = studentsWithAttendance.filter(s => s.attendance.length === 0).length;

      console.log(`Attendance Summary - Marked: ${markedCount}, Not Marked: ${notMarkedCount}`);

      setStudents(studentsWithAttendance);
      setTotalCount(studentsWithAttendance.length);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to fetch students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [selectedDate, selectedLevel, selectedGroup]);

  // Filter students based on selected level and group
  const filteredStudents = students.filter(student => {
    const levelMatch = selectedLevel === 'all' || student.level.toString() === selectedLevel;
    const groupMatch = selectedGroup === 'all' || student.group === selectedGroup;
    return levelMatch && groupMatch;
  });

  // Calculate attendance statistics
  const attendanceStats = {
    total: filteredStudents.length,
    present: filteredStudents.filter(student => 
      student.attendance.some(a => a.Status === 'present')
    ).length,
    absent: filteredStudents.filter(student => 
      student.attendance.some(a => a.Status === 'absent')
    ).length,
    late: filteredStudents.filter(student => 
      student.attendance.some(a => a.Status === 'late')
    ).length
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);

      await ensureAuth();

      // Fetch all students
      const studentsResponse = await databases.listDocuments(
        DATABASE_ID,
        STUDENTS_COLLECTION_ID,
        [
          ...(selectedLevel !== 'all' ? [Query.equal('level', selectedLevel)] : []),
          ...(selectedGroup !== 'all' ? [Query.equal('group', selectedGroup)] : [])
        ]
      );

      // Process the data
      const exportData: MonthlyAttendanceData[] = studentsResponse.documents.map((student: any) => ({
        studentId: student.$id,
        studentName: student.name,
        level: student.level,
        registerNumber: student.register_num,
        attendance: student.attendance || []
      }));

      // Generate and download CSV
      const csv = generateCSV(exportData);
      const filename = `attendance_summary.csv`;
      downloadCSV(csv, filename);

    } catch (err) {
      console.error('Error exporting attendance data:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to export attendance data');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateAttendance = async (studentId: string, status: 'present' | 'absent' | 'late') => {
    if (!editingAttendance) return;

    try {
      setError(null);

      console.log(`Updating attendance for student ${studentId} to ${status} on ${selectedDate}`);

      // Call updateAttendance with the selected date
      const result = await updateAttendance(
        studentId,
        {
          status: status,
          remarks: editingAttendance.remarks || ''
        },
        selectedDate // Pass the date to update attendance for
      );

      console.log('Update result:', result);

      // Close editing mode first
      setEditingAttendance(null);

      // Refresh the attendance data after a short delay to ensure database is updated
      setTimeout(async () => {
        await fetchStudents();
      }, 500);

    } catch (error: any) {
      console.error('Error updating attendance:', error);
      setError(error?.message || 'Failed to update attendance. Please try again.');
    }
  };

  const handleRemarksChange = (studentId: string, remarks: string) => {
    if (!editingAttendance) return;
    setEditingAttendance({
      ...editingAttendance,
      remarks
    });
  };

  const handleRemarksKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, studentId: string) => {
    if (e.key === 'Enter' && editingAttendance) {
      e.preventDefault();
      await handleUpdateAttendance(studentId, editingAttendance.status);
    } else if (e.key === 'Escape') {
      setEditingAttendance(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Attendance Summary</h1>
            <button
              onClick={handleExport}
              disabled={exporting}
              className={`px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2 ${
                exporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Download size={18} />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
          
          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full h-10 rounded-md border-gray-300 shadow-sm p-2 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="w-full h-10 p-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">All Levels</option>
                {[1, 2, 3, 4].map((level) => (
                  <option key={level} value={level.toString()}>Level {level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full p-2 h-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={selectedLevel === 'all'}
              >
                <option value="all">All Groups</option>
                {getAvailableGroups().map(group => (
                  <option key={group.value} value={group.value}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <h3 className="text-lg font-medium text-green-800">Present</h3>
              <p className="text-3xl font-bold text-green-600">
                {filteredStudents.reduce((acc, student) => acc + (student.attendance.some(a => a.Status === 'present') ? 1 : 0), 0)}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
              <h3 className="text-lg font-medium text-yellow-800">Late</h3>
              <p className="text-3xl font-bold text-yellow-600">
                {filteredStudents.reduce((acc, student) => acc + (student.attendance.some(a => a.Status === 'late') ? 1 : 0), 0)}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-100">
              <h3 className="text-lg font-medium text-red-800">Absent</h3>
              <p className="text-3xl font-bold text-red-600">
                {filteredStudents.reduce((acc, student) => acc + (student.attendance.some(a => a.Status === 'absent') ? 1 : 0), 0)}
              </p>
            </div>
            <div className="bg-pink-50 rounded-lg p-4 border border-blue-100">
              <h3 className="text-lg font-medium text-pink-800">Total</h3>
              <p className="text-3xl font-bold text-pink-600">
                {filteredStudents.length}
              </p>
            </div>
          </div>

          {/* Attendance Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => {
                  const latestAttendance = student.attendance[0];
                  return (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">
                            {student.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingAttendance?.studentId === student.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleUpdateAttendance(student.id, 'present')}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                editingAttendance.status === 'present'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              Present
                            </button>
                            <button
                              onClick={() => handleUpdateAttendance(student.id, 'late')}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                editingAttendance.status === 'late'
                                  ? 'bg-yellow-600 text-white'
                                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              }`}
                            >
                              Late
                            </button>
                            <button
                              onClick={() => handleUpdateAttendance(student.id, 'absent')}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                editingAttendance.status === 'absent'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                            >
                              Absent
                            </button>
                            <button
                              onClick={() => setEditingAttendance(null)}
                              className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                latestAttendance?.Status === 'present'
                                  ? 'bg-green-100 text-green-800'
                                  : latestAttendance?.Status === 'absent'
                                  ? 'bg-red-100 text-red-800'
                                  : latestAttendance?.Status === 'late'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {latestAttendance?.Status || 'Not Marked'}
                            </span>
                            <button
                              onClick={() => setEditingAttendance({ 
                                studentId: student.id, 
                                status: latestAttendance?.Status || 'present',
                                remarks: latestAttendance?.remarks || ''
                              })}
                              className="text-pink-600 hover:text-pink-800 transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingAttendance?.studentId === student.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm w-full focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Add remarks (optional)"
                              value={editingAttendance.remarks || ''}
                              onChange={(e) => handleRemarksChange(student.id, e.target.value)}
                              onKeyDown={(e) => handleRemarksKeyDown(e, student.id)}
                              autoFocus
                            />
                            <button
                              onClick={() => handleUpdateAttendance(student.id, editingAttendance.status)}
                              className="px-3 py-1 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingAttendance(null)}
                              className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {latestAttendance?.remarks || '-'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default AttendanceSummaryPage; 